import { useCallback, useEffect, useRef } from 'react';

import { HINTS } from './hint-config.js';
import {
  isNodeSeen,
  isTourLightsUnlocked,
  openHintDialog,
  useHintState,
} from './hint-store.js';
import { registerHintTarget, unregisterHintTarget } from './hint-targets.js';

const GATED_EVENT_TYPES = ['pointerdown', 'click', 'keydown'] as const;

/**
 * How a target behaves on its first interaction: `gate` teaches before acting - the first
 * activation opens the hint dialog instead of running the element's action - while `path`
 * only carries a beacon and always lets the interaction through. Menu items and tabs are
 * path targets: Radix owns their pointer and keyboard handling, and swallowing an item's
 * first click there would strand the user mid-flow.
 */
export type HintTargetMode = 'gate' | 'path';

function isActivationKey(event: KeyboardEvent): boolean {
  return event.key === 'Enter' || event.key === ' ';
}

/** True for an unmodified primary-button pointer or click activation. */
function isUnmodifiedPrimaryButton(event: MouseEvent | PointerEvent): boolean {
  return (
    event.button === 0 &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/**
 * Attaches an onboarding hint target to an interactive element: registers it in the
 * hint-targets registry, so `HintBeaconLayer` can position a beacon on it, and — for a
 * `gate` target whose hint is still unseen — gates the element's first interaction behind
 * the hint dialog instead of letting it reach the element's own handler.
 *
 * The gate is a capture-phase listener attached directly to the element for pointerdown,
 * click, and keydown (Enter/Space only). Capture runs ahead of React's bubble-phase
 * delegated handlers, so the very first interaction opens the dialog instead of performing
 * the element's real action; a pointerdown or keydown gate also swallows the click event
 * that follows it in the same gesture, so the underlying action never fires alongside the
 * dialog. The gate stays inactive until tour lights unlock (same soft-open delay the old
 * highlight used), and once the hint is marked seen, later interactions pass through
 * untouched. A `path` target never gets the listener at all. This replaces the old
 * highlight-and-overlay approach - no `inert`, no overlay button, no `cloneElement`.
 *
 * An id that is not in `HINTS` is inert: nothing registers and nothing is gated.
 *
 * Returns a ref callback; attach it to the element the hint describes.
 */
export function useHintTarget<T extends HTMLElement>(
  id: string,
  mode: HintTargetMode,
): (node: T | null) => void {
  const elementRef = useRef<T | null>(null);
  const listenerRef = useRef<((event: Event) => void) | null>(null);
  const gestureGatedRef = useRef(false);
  const { activeDialogHintId } = useHintState();

  /** A gated pointerdown/keydown sets a flag so the synthetic click in the same gesture is
   *  swallowed. Once the dialog closes, clear the flag so the next real activation is not
   *  blocked if the browser suppressed that follow-up click. */
  useEffect(() => {
    if (activeDialogHintId === null && gestureGatedRef.current) {
      gestureGatedRef.current = false;
    }
  }, [activeDialogHintId]);

  return useCallback(
    (node: T | null) => {
      if (!(id in HINTS)) return;

      const previousElement = elementRef.current;
      const previousListener = listenerRef.current;
      if (previousElement && previousListener) {
        for (const eventType of GATED_EVENT_TYPES) {
          previousElement.removeEventListener(
            eventType,
            previousListener,
            true,
          );
        }
      }
      if (previousElement) unregisterHintTarget(id, previousElement);

      elementRef.current = node;

      if (node === null) {
        listenerRef.current = null;
        return;
      }

      if (mode === 'gate') {
        const listener = (event: Event): void => {
          if (
            event.type === 'keydown' &&
            !isActivationKey(event as KeyboardEvent)
          )
            return;

          if (
            (event.type === 'pointerdown' || event.type === 'click') &&
            !isUnmodifiedPrimaryButton(event as MouseEvent)
          ) {
            return;
          }

          if (event.type === 'click' && gestureGatedRef.current) {
            gestureGatedRef.current = false;
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          if (isNodeSeen(id) || !isTourLightsUnlocked()) return;

          event.preventDefault();
          event.stopPropagation();
          openHintDialog(id);
          gestureGatedRef.current = event.type !== 'click';
        };
        listenerRef.current = listener;
        for (const eventType of GATED_EVENT_TYPES) {
          node.addEventListener(eventType, listener, true);
        }
      } else {
        listenerRef.current = null;
      }

      registerHintTarget(id, node);
    },
    [id, mode],
  );
}
